// ============================================================
// CLUBHUB – Kalender: wer eingeladen ist und wer was sieht
// ============================================================
// Reine Regeln, ohne Datenbank und ohne Next — dieselben fuer den
// Musterbestand, die Datenbank, das Kalender-Abo und die Tests.
//
// EINGELADEN sind die Gruppen eines Termins samt allen Untergruppen
// und die einzeln eingeladenen Personen (`EventContact`). Ohne beides
// betrifft der Termin alle Mitglieder (ACTIVE, PASSIVE, HONORARY) —
// die ClubDesk-Vorgabe „Termine ohne Teilnehmer betreffen alle
// Kontakte“, bei uns auf Mitglieder begrenzt.
//
// SICHTBAR im Verwaltungsbereich (dort hat jeder `members.read`):
//   PUBLIC, MEMBERS, GROUPS  alle mit Zugang zum Kalender
//   BOARD                    Vorstand und Verwaltung (Rollen unten)
//   PRIVATE                  nur wer den Termin angelegt hat
// ============================================================
import type { ClubRole, Visibility } from "@/generated/prisma/enums";
import type { ContactView } from "@/lib/views";
import type { PersonKurz } from "./views";

export const MITGLIED_STATUS = new Set<string>(["ACTIVE", "PASSIVE", "HONORARY"]);

/** Rollen, die Termine „nur Vorstand und Verwaltung“ sehen. */
export const VORSTAND_ROLLEN = new Set<ClubRole>(["OWNER", "ADMIN", "BOARD", "TREASURER", "SECRETARY"]);

type GruppeKnoten = { id: string; parentId: string | null };

/** Die Gruppe samt allen Untergruppen (beliebig tief). */
export function mitUntergruppen(gruppen: GruppeKnoten[], ids: Iterable<string>): Set<string> {
  const ergebnis = new Set(ids);
  let gewachsen = true;
  while (gewachsen) {
    gewachsen = false;
    for (const g of gruppen) {
      if (g.parentId && ergebnis.has(g.parentId) && !ergebnis.has(g.id)) {
        ergebnis.add(g.id);
        gewachsen = true;
      }
    }
  }
  return ergebnis;
}

/**
 * Der Kreis einer Gruppe im Kalenderfilter: sie selbst, ihre
 * Obergruppe und ihre Untergruppen — die U13 sieht das
 * Nikolausturnier der Jugend.
 */
export function gruppenKreis(gruppen: GruppeKnoten[], id: string): Set<string> {
  const kreis = mitUntergruppen(gruppen, [id]);
  const g = gruppen.find((x) => x.id === id);
  if (g?.parentId) kreis.add(g.parentId);
  return kreis;
}

/** Wer zu einem Termin eingeladen ist (siehe Kopf). */
export function eingeladeneAus(
  kontakte: ContactView[],
  gruppen: GruppeKnoten[],
  groupIds: string[],
  contactIds: string[] = [],
): ContactView[] {
  if (groupIds.length === 0 && contactIds.length === 0) {
    return kontakte.filter((c) => c.kind === "PERSON" && MITGLIED_STATUS.has(c.status));
  }
  const ids = mitUntergruppen(gruppen, groupIds);
  const einzeln = new Set(contactIds);
  return kontakte.filter(
    (c) => c.status !== "FORMER" && c.status !== "DECEASED" && (einzeln.has(c.id) || c.groups.some((g) => ids.has(g.groupId))),
  );
}

/** Fuer das Formular: die Kontakt-IDs je Gruppe (direkt) und wie viele „alle Mitglieder“ sind. */
export type EinladungsBasis = {
  alle: number;
  /** Kontakte je Gruppe, nur direkte Zugehoerigkeit. */
  jeGruppe: Record<string, string[]>;
  gruppen: GruppeKnoten[];
};

export function einladungsBasis(kontakte: ContactView[], gruppen: GruppeKnoten[]): EinladungsBasis {
  const jeGruppe: Record<string, string[]> = {};
  for (const c of kontakte) {
    if (c.status === "FORMER" || c.status === "DECEASED") continue;
    for (const g of c.groups) (jeGruppe[g.groupId] ??= []).push(c.id);
  }
  return {
    alle: kontakte.filter((c) => c.kind === "PERSON" && MITGLIED_STATUS.has(c.status)).length,
    jeGruppe,
    gruppen: gruppen.map((g) => ({ id: g.id, parentId: g.parentId })),
  };
}

/** Wie viele eine Auswahl im Formular einlaedt — dieselbe Regel wie `eingeladeneAus`. */
export function eingeladenZahl(basis: EinladungsBasis, groupIds: string[], contactIds: string[]): number {
  if (groupIds.length === 0 && contactIds.length === 0) return basis.alle;
  const ids = new Set(contactIds);
  for (const g of mitUntergruppen(basis.gruppen, groupIds)) for (const c of basis.jeGruppe[g] ?? []) ids.add(c);
  return ids.size;
}

function alterAm(geboren: string | null, heute: string): number | null {
  if (!geboren) return null;
  return Number(heute.slice(0, 4)) - Number(geboren.slice(0, 4)) - (heute.slice(5) < geboren.slice(5) ? 1 : 0);
}

/** Eine Person in der Kurzform der Zusage-, Anwesenheits- und Helferlisten. */
export function personKurz(c: ContactView, groupIds: string[], heute: string): PersonKurz {
  const funktion = c.groups.find((g) => groupIds.includes(g.groupId) && g.function)?.function ?? null;
  const alter = alterAm(c.birthDate, heute);
  return {
    id: c.id,
    name: c.displayName,
    firstName: c.firstName ?? "",
    lastName: c.lastName ?? c.displayName,
    memberNumber: c.memberNumber,
    funktion,
    consentInternet: c.consentInternet,
    minderjaehrig: alter !== null && alter < 18,
    ...(c.status === "PROSPECT" ? { gast: true } : {}),
  };
}

/** Wer sieht einen Termin im Verwaltungsbereich (siehe Kopf)? */
export function darfSehen(
  t: { visibility: Visibility; createdById?: string | null },
  wer: { role: ClubRole; userId: string },
): boolean {
  switch (t.visibility) {
    case "PUBLIC":
    case "MEMBERS":
    case "GROUPS":
      return true;
    case "BOARD":
      return VORSTAND_ROLLEN.has(wer.role) || t.createdById === wer.userId;
    case "PRIVATE":
      return t.createdById === wer.userId;
  }
}

/**
 * Personen fuer die Auswahlfelder im Browser — nur, was die Auswahl
 * zeigt und durchsucht (Name, Nummer, Ort, Status). Anschrift,
 * Geburtstag, Telefon und Notizen bleiben auf dem Server.
 */
export function fuerAuswahl(c: ContactView): ContactView {
  return {
    ...c,
    salutation: null,
    title: null,
    birthDate: null,
    gender: null,
    street: null,
    zip: null,
    email: null,
    phone: null,
    mobile: null,
    householdId: null,
    feeTypeId: null,
    hasMandate: false,
    openAmount: 0,
    notes: null,
    customFields: {},
    groups: c.groups.map((g) => ({ ...g, since: null })),
  };
}

/** Was eine Abo-Adresse zeigt (siehe `lib/kalender/ical.ts`). */
export type FeedSicht =
  | { scope: "PUBLIC" }
  | { scope: "GROUP"; groupId: string }
  | { scope: "PERSONAL"; kontakt: { id: string; status: string; groupIds: string[] } };

/**
 * Die Termine eines Abos:
 *   oeffentlich  nur PUBLIC
 *   Gruppe       Termine der Gruppe, ihrer Ober- und Untergruppen und
 *                die fuer alle (PUBLIC, MEMBERS, GROUPS); BOARD nur,
 *                wenn die Gruppe selbst eingeladen ist
 *   persoenlich  wozu die Person eingeladen ist (Gruppe samt
 *                Untergruppen, einzeln, oder als Mitglied „fuer alle“);
 *                BOARD nur mit Einladung
 * PRIVATE steht nie in einem Abo.
 */
export function termineFuerFeed<T extends { visibility: Visibility; groupIds: string[]; contactIds: string[] }>(
  termine: T[],
  sicht: FeedSicht,
  gruppen: GruppeKnoten[],
): T[] {
  if (sicht.scope === "PUBLIC") return termine.filter((t) => t.visibility === "PUBLIC");
  return termine.filter((t) => {
    if (t.visibility === "PRIVATE") return false;
    const fuerAlle = t.groupIds.length === 0 && t.contactIds.length === 0;
    if (sicht.scope === "GROUP") {
      if (t.visibility === "BOARD") return t.groupIds.includes(sicht.groupId);
      if (fuerAlle) return true;
      const kreis = gruppenKreis(gruppen, sicht.groupId);
      return t.groupIds.some((g) => kreis.has(g));
    }
    const k = sicht.kontakt;
    if (k.status === "FORMER" || k.status === "DECEASED") return false;
    if (fuerAlle) return t.visibility !== "BOARD" && MITGLIED_STATUS.has(k.status);
    if (t.contactIds.includes(k.id)) return true;
    const eingeladen = mitUntergruppen(gruppen, t.groupIds);
    return k.groupIds.some((g) => eingeladen.has(g));
  });
}
